import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faCalendarDays,
  faClock,
  faBolt,
  faBrain,
  faFireFlameCurved,
  faHeartPulse,
  faLayerGroup,
  faListCheck,
  faTag,
  faSun,
  faCloudSun,
  faMoon,
} from "@fortawesome/free-solid-svg-icons";
import "./HabitModal.css";

const DEFAULT_FORM = {
  nome: "",
  tipo: "simples",
  frequencia_tipo: "todos_os_dias",
  dias_semana: [],
  periodo_dia: "qualquer_horario",
  pilar: "foco",
  habito_ancora: "",
  categoria: "",
  grupo_rotina: "",
  meta_quantidade: "",
  meta_unidade: "minutos",
  periodo_meta: "dia",
};

const FREQUENCY_OPTIONS = [
  { value: "todos_os_dias", label: "Todos os dias" },
  { value: "dias_semana", label: "Dias da semana" },
  { value: "vezes_periodo", label: "Vezes por periodo" },
];

const DAY_PERIOD_OPTIONS = [
  { value: "qualquer_horario", label: "Qualquer horario", icon: faClock },
  { value: "manha", label: "Manha", icon: faSun },
  { value: "tarde", label: "Tarde", icon: faCloudSun },
  { value: "noite", label: "Noite", icon: faMoon },
];

const GOAL_UNIT_OPTIONS = ["minutos", "litros", "paginas", "vezes"];
const WEEKDAY_OPTIONS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sab" },
];

const PILLAR_OPTIONS = [
  { value: "energia", label: "Energia", icon: faBolt },
  { value: "foco", label: "Foco", icon: faBullseye },
  { value: "clareza", label: "Clareza", icon: faBrain },
  { value: "saude_mental", label: "Saude mental", icon: faHeartPulse },
  { value: "execucao", label: "Execucao", icon: faListCheck },
];

const HabitModal = ({ show, onClose, onSave, habit, saving }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!show) return;

    if (habit) {
      setForm({
        nome: habit.nome || "",
        tipo: habit.tipo || "simples",
        frequencia_tipo: habit.frequencia_tipo || "todos_os_dias",
        dias_semana: Array.isArray(habit.dias_semana) ? habit.dias_semana.map(Number) : [],
        periodo_dia: habit.periodo_dia || "qualquer_horario",
        pilar: habit.pilar || "foco",
        habito_ancora: habit.habito_ancora || "",
        categoria: habit.categoria || "",
        grupo_rotina: habit.grupo_rotina || "",
        meta_quantidade: habit.meta_quantidade ?? "",
        meta_unidade: habit.meta_unidade || "minutos",
        periodo_meta: habit.periodo_meta || "dia",
      });
    } else {
      setForm(DEFAULT_FORM);
    }

    setErrorMessage("");
  }, [show, habit]);

  const isFlexibleGoal = useMemo(() => form.tipo === "meta_flexivel", [form.tipo]);
  const isWeeklyFrequency = useMemo(() => form.frequencia_tipo === "dias_semana", [form.frequencia_tipo]);

  const toggleWeekday = (weekday) => {
    setForm((current) => ({
      ...current,
      dias_semana: current.dias_semana.includes(weekday)
        ? current.dias_semana.filter((item) => item !== weekday)
        : [...current.dias_semana, weekday].sort((left, right) => left - right),
    }));
  };

  const handleSubmit = async () => {
    const normalizedName = form.nome.trim();

    if (!normalizedName) {
      setErrorMessage("Informe o nome do habito.");
      return;
    }

    if (isFlexibleGoal && !form.meta_quantidade) {
      setErrorMessage("Defina a meta do habito flexivel.");
      return;
    }

    if (isWeeklyFrequency && form.dias_semana.length === 0) {
      setErrorMessage("Selecione pelo menos um dia da semana para esse habito.");
      return;
    }

    setErrorMessage("");
    await onSave?.({
      ...form,
      nome: normalizedName,
      dias_semana: isWeeklyFrequency ? form.dias_semana : [],
      meta_quantidade: isFlexibleGoal && form.meta_quantidade ? Number(form.meta_quantidade) : null,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" dialogClassName="habit-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title className="habit-modal-title">
          <span className="habit-modal-icon">
            <FontAwesomeIcon icon={faFireFlameCurved} />
          </span>
          <span>
            {habit ? "Editar Habito" : "Novo Habito"}
            <small>{habit ? "Altere o nome, frequencia ou meta do habito" : "Crie um ritmo simples para manter consistencia"}</small>
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <div className="habit-type-switcher">
          <button
            type="button"
            className={form.tipo === "simples" ? "active" : ""}
            onClick={() => setForm((current) => ({ ...current, tipo: "simples" }))}
          >
            Habito simples
          </button>
          <button
            type="button"
            className={form.tipo === "meta_flexivel" ? "active" : ""}
            onClick={() => setForm((current) => ({ ...current, tipo: "meta_flexivel" }))}
          >
            Meta flexivel
          </button>
        </div>

        <Form.Group className="mt-4">
          <Form.Label className="habit-section-label">Nome do habito</Form.Label>
          <Form.Control
            className="habit-name-input"
            value={form.nome}
            onChange={(event) => setForm((current) => ({ ...current, nome: event.target.value }))}
            placeholder="Ex.: Estudar ingles"
          />
        </Form.Group>

        <section className="habit-modal-section">
          <div className="habit-section-header">
            <span className="habit-section-icon">
              <FontAwesomeIcon icon={faCalendarDays} />
            </span>
            <div>
              <strong>Frequencia</strong>
              <small>Defina se esse habito acontece todos os dias, em dias especificos ou por meta.</small>
            </div>
          </div>

          <Form.Select
            className="habit-section-select"
            value={form.frequencia_tipo}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                frequencia_tipo: event.target.value,
                dias_semana: event.target.value === "dias_semana" ? current.dias_semana : [],
              }))
            }
          >
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>

          {isWeeklyFrequency && (
            <>
              <Form.Label className="habit-section-label mt-3">Dias da semana</Form.Label>
              <div className="habit-weekday-grid">
                {WEEKDAY_OPTIONS.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    className={`habit-weekday-chip ${form.dias_semana.includes(day.value) ? "active" : ""}`}
                    onClick={() => toggleWeekday(day.value)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="habit-modal-section">
          <div className="habit-section-header">
            <span className="habit-section-icon">
              <FontAwesomeIcon icon={faClock} />
            </span>
            <div>
              <strong>Periodo do dia</strong>
              <small>Ajude seu cerebro a encaixar esse habito na rotina certa.</small>
            </div>
          </div>

          <div className="habit-period-grid">
            {DAY_PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`habit-period-chip ${form.periodo_dia === option.value ? "active" : ""}`}
                onClick={() => setForm((current) => ({ ...current, periodo_dia: option.value }))}
              >
                <FontAwesomeIcon icon={option.icon} />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="habit-modal-section">
          <div className="habit-section-header">
            <span className="habit-section-icon target">
              <FontAwesomeIcon icon={faBullseye} />
            </span>
            <div>
              <strong>Pilar de performance</strong>
              <small>Classifique o impacto desse habito para o score diario e para a revisao semanal.</small>
            </div>
          </div>

          <div className="habit-pillar-grid">
            {PILLAR_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`habit-pillar-chip ${form.pilar === option.value ? "active" : ""}`}
                onClick={() => setForm((current) => ({ ...current, pilar: option.value }))}
              >
                <FontAwesomeIcon icon={option.icon} />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="habit-modal-section">
          <div className="habit-section-header">
            <span className="habit-section-icon">
              <FontAwesomeIcon icon={faTag} />
            </span>
            <div>
              <strong>Contexto</strong>
              <small>Organize por categoria, rotina e gatilho para manter sua visao limpa.</small>
            </div>
          </div>

          <div className="habit-modal-grid compact">
            <Form.Group>
              <Form.Label className="habit-section-label">
                <FontAwesomeIcon icon={faListCheck} /> Categoria
              </Form.Label>
              <Form.Control
                value={form.categoria}
                onChange={(event) => setForm((current) => ({ ...current, categoria: event.target.value }))}
                placeholder="Ex.: Educacao"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="habit-section-label">
                <FontAwesomeIcon icon={faLayerGroup} /> Rotina (grupo)
              </Form.Label>
              <Form.Control
                value={form.grupo_rotina}
                onChange={(event) => setForm((current) => ({ ...current, grupo_rotina: event.target.value }))}
                placeholder="Ex.: Manha"
              />
            </Form.Group>

            <Form.Group className="habit-grid-span-2">
              <Form.Label className="habit-section-label">
                <FontAwesomeIcon icon={faBolt} /> Habito ancora
              </Form.Label>
              <Form.Control
                value={form.habito_ancora}
                onChange={(event) => setForm((current) => ({ ...current, habito_ancora: event.target.value }))}
                placeholder="Ex.: Depois do cafe, antes de abrir o notebook"
              />
            </Form.Group>
          </div>
        </section>

        {isFlexibleGoal && (
          <section className="habit-modal-section accent">
            <div className="habit-section-header">
              <span className="habit-section-icon target">
                <FontAwesomeIcon icon={faBullseye} />
              </span>
              <div>
                <strong>Meta flexivel</strong>
                <small>Defina um alvo numerico para medir progresso em vez de check simples.</small>
              </div>
            </div>

            <div className="habit-modal-grid compact">
              <Form.Group>
                <Form.Label className="habit-section-label">Meta</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.meta_quantidade}
                  onChange={(event) => setForm((current) => ({ ...current, meta_quantidade: event.target.value }))}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="habit-section-label">Unidade</Form.Label>
                <Form.Select
                  value={form.meta_unidade}
                  onChange={(event) => setForm((current) => ({ ...current, meta_unidade: event.target.value }))}
                >
                  {GOAL_UNIT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="habit-grid-span-2">
                <Form.Label className="habit-section-label">Periodo da meta</Form.Label>
                <Form.Select
                  value={form.periodo_meta}
                  onChange={(event) => setForm((current) => ({ ...current, periodo_meta: event.target.value }))}
                >
                  <option value="dia">Por dia</option>
                  <option value="semana">Por semana</option>
                  <option value="mes">Por mes</option>
                </Form.Select>
              </Form.Group>
            </div>
          </section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HabitModal;
